// The indoor unit corner: every Daikin VRV indoor unit, with the ceiling it
// needs, its size in tons, its dimensions, when to use it and its limits.
//
// Used by components/IndoorUnitCorner.jsx (the architects page),
// components/IndoorUnitLooks.jsx (the VRV page: the look and finish, from
// LOOKS and UNIT_LOOKS at the end of this file), components/CeilingChecker.jsx,
// and scripts/prerender.js (the same text for Google). No imports, so the
// pre-render can load it.
//
// Sources: Daikin India VRV Alpha catalogue (checked 10 Oct 2026). One
// exception: the catalogue prints the 4-way ceiling suspended unit's heating
// figures (30,700 / 42,700 Btu/h = 9.0 / 12.5 kW) in its cooling row, so its
// cooling (8.0 / 11.2 kW) is from Daikin's data for the same models
// (daikinmea.com, FXUQ-A). Nothing here is estimated: a figure Daikin does
// not publish is left out.
//
// Tons: Daikin's rated cooling in Btu/h ÷ 12,000, to one decimal.
//
// Ceiling to plan for: Citi Cool site practice (Saheb, 10 Oct 2026). A unit
// never finishes at its bare height. The 1-way cassette is 175 mm but
// finishes at 8–9 inches, because the team needs room to service it later.
// The same rule runs through every ceiling unit, and Saheb confirmed each
// figure: Daikin's ceiling figure (or the unit height + 30 mm where Daikin
// gives none) plus 1 to 2 inches, in whole inches. Ducts crossing below are
// extra.

export const UNIT_GROUPS = [
  { id: 'ceiling', title: 'Hidden in the ceiling', sub: 'Ducted units: only the grilles show' },
  { id: 'cassette', title: 'Cassettes', sub: 'A grille set into the false ceiling' },
  { id: 'open', title: 'No false ceiling needed', sub: 'Under the slab or on the wall' },
  { id: 'floor', title: 'On the floor', sub: 'Under windows and along glass' },
  { id: 'big', title: 'Big spaces and spot cooling', sub: 'Halls, auditoriums, single spots' },
];

// size = [model, cooling Btu/h]
export const VRV_INDOOR_UNITS = [
  {
    id: 'slim-duct', group: 'ceiling', kind: 'duct',
    name: 'Slim duct (low static)', series: 'FXDQ',
    sizes: [['FXDQ20PDV36', 7500], ['FXDQ25PDV36', 9600], ['FXDQ32PDV36', 12300], ['FXDQ40NDV36', 15400], ['FXDQ50NDV36', 19100], ['FXDQ63NDV36', 24200]],
    unitMm: 200, plan: [11, 12], daikinMin: 240,
    dims: ['200 × 700 × 620 mm (0.6–1.0 TR)', '200 × 900 × 620 mm (1.3–1.6 TR)', '200 × 1,100 × 620 mm (2.0 TR)'],
    shows: 'Only the grilles',
    best: ['Bedrooms', 'Hotel rooms', 'Cabins'],
    use: [
      'Quiet rooms: one of the quietest units, 29–36 dB(A)',
      'Low false ceilings: the unit is only 200 mm high',
      'No long ducts: a short plenum and grilles right beside the unit',
    ],
    limits: [
      '2 TR at most',
      'Low static pressure: short plenums only, no long duct runs',
      'Gentle air throw, so a big room takes longer to cool',
    ],
    specs: [
      ['Static pressure', '10–44 Pa (low)'],
      ['Sound', '29–36 dB(A)'],
      ['Airflow', '6.4–16.5 m³/min'],
      ['Drain pump', 'Standard, lifts 750 mm'],
      ['Weight', '23–31 kg'],
      ['Note', 'Return air from below is 5 dB(A) louder than from the back'],
    ],
  },
  {
    id: 'mid-duct', group: 'ceiling', kind: 'duct',
    name: 'Mid static duct', series: 'FXMQ-A',
    sizes: [['FXMQ40ARV16', 15400], ['FXMQ50ARV16', 19100], ['FXMQ63ARV16', 24200], ['FXMQ80ARV16', 30700], ['FXMQ100ARV16', 38200]],
    unitMm: 300, plan: [14, 15], ducts: true,
    dims: ['300 × 700 × 700 mm', '300 × 1,000 × 700 mm (larger sizes)'],
    shows: 'Only the grilles',
    best: ['Living rooms', 'Large bedrooms', 'Cabins'],
    use: [
      'A short duct to two or three grilles',
      'Linear slot diffusers along a window or wall',
      'More push than the slim duct, for bigger rooms',
    ],
    limits: [
      '300 mm unit, plus the depth of the ducts',
      '37–44 dB(A): keep it away from bed heads',
      'Ducts and grilles need designing',
    ],
    specs: [
      ['Static pressure', '30–60 Pa (medium)'],
      ['Sound', '37–44 dB(A)'],
      ['Airflow', '12–34 m³/min'],
      ['Drain pump', 'Standard, lifts 700 mm'],
      ['Weight', '27–36 kg'],
    ],
  },
  {
    id: 'high-duct', group: 'ceiling', kind: 'duct',
    name: 'High static duct', series: 'FXMQ-P',
    sizes: [['FXMQ20PBV36', 7500], ['FXMQ25PBV36', 9600], ['FXMQ32PBV36', 12300], ['FXMQ40PBV36', 15400], ['FXMQ50PBV36', 19100], ['FXMQ63PBV36', 24200], ['FXMQ80PBV36', 30700], ['FXMQ100PBV36', 38200], ['FXMQ125PBV36', 47800], ['FXMQ140PBV36', 54600]],
    unitMm: 300, plan: [14, 15], ducts: true,
    dims: ['300 × 700 × 700 mm (smaller sizes)', '300 × 1,000–1,400 × 700 mm (larger sizes)'],
    shows: 'Only the grilles',
    best: ['Open offices', 'Showrooms', 'Halls'],
    use: [
      'Long duct runs and many grilles',
      'The unit can sit over a passage or toilet, away from the room',
      'Fresh air can be mixed in',
    ],
    limits: [
      'Needs a proper duct design: sizes, dampers, insulation',
      'A deeper ceiling wherever ducts cross',
      'Keep the unit away from quiet rooms',
    ],
    specs: [
      ['Static pressure', '30–200 Pa by size (high)'],
      ['Sound', '29–46 dB(A)'],
      ['Airflow', 'Up to 46 m³/min'],
      ['Drain pump', 'Standard, lifts 700 mm'],
      ['Weight', '27–46 kg'],
    ],
  },
  {
    id: 'one-way-cassette', group: 'cassette', kind: 'corner',
    name: '1-way cassette (corner)', series: 'FXKQ',
    sizes: [['FXKQ32ARV16', 12300], ['FXKQ40ARV16', 15400], ['FXKQ50ARV16', 19100], ['FXKQ63ARV16', 24200]],
    unitMm: 175, drawMm: 145, plan: [8, 9], backGapMm: 75,
    unitNote: '145 mm unit + 30 mm clearance (Daikin)',
    dims: ['175 mm high, with clearance', 'Panel 1,390 × 595 mm, 41 mm thick'],
    shows: 'A slim panel along the wall',
    best: ['Bedrooms', 'Hotel rooms', 'Cabins'],
    use: [
      'The lowest ceiling of any ceiling unit: it finishes at 8–9 inches',
      'A seamless fit along a wall or in a corner',
      'Bulkheads, narrow ceilings and window lines',
    ],
    limits: [
      '2 TR at most',
      'Blows one way only: place it to throw across the room',
      'Leave 3 inches (75 mm) behind it, from the wall',
    ],
    specs: [
      ['Back gap', '3 in (75 mm) between the back of the unit and the wall'],
      ['Spacers', '25 mm each, up to 75 mm: for ceilings as shallow as 100 mm'],
      ['Panel', 'White or silver'],
    ],
  },
  {
    id: 'compact-cassette', group: 'cassette', kind: 'cassette',
    name: '600 × 600 cassette', series: 'FXZQ',
    sizes: [['FXZQ20CRV16', 7500], ['FXZQ25CRV16', 9600], ['FXZQ32CRV16', 12300], ['FXZQ40CRV16', 15400], ['FXZQ50CRV16', 19100]],
    unitMm: 260, plan: [13, 14],
    dims: ['260 × 575 × 575 mm', 'Fits one 600 × 600 ceiling tile'],
    shows: 'Flush in one ceiling tile',
    best: ['Offices', 'Meeting rooms', 'Shops'],
    use: [
      'Grid ceilings: fits one 600 × 600 tile, only 8 mm below it',
      'Lights, speakers and sprinklers can sit in the next tile',
      'Air in four directions from the centre of a room',
    ],
    limits: [
      '1.6 TR at most',
      'Needs 13–14 inches above the ceiling',
      'The grille shows in the ceiling',
    ],
    specs: [
      ['Sound', '25.5–43 dB(A)'],
      ['Airflow', '6.5–14.5 m³/min'],
      ['Weight', '15.5–18.5 kg'],
      ['Options', 'Presence and floor sensor; fresh-air kit'],
    ],
  },
  {
    id: 'round-flow-cassette', group: 'cassette', kind: 'cassette',
    name: 'Round flow cassette', series: 'FXFSQ',
    sizes: [['FXFSQ25ARV16', 9600], ['FXFSQ32ARV16', 12300], ['FXFSQ40ARV16', 15400], ['FXFSQ50ARV16', 19100], ['FXFSQ63ARV16', 24200], ['FXFSQ80ARV16', 30700], ['FXFSQ100ARV16', 38200], ['FXFSQ125ARV16', 47800], ['FXFSQ140ARV16', 54600]],
    unitMm: 298, unitText: '256–298 mm', plan: [13, 15],
    dims: ['256 or 298 × 840 × 840 mm, by size', 'Panel 950 × 950 mm'],
    shows: 'A 950 × 950 mm panel',
    best: ['Open offices', 'Showrooms', 'Large living rooms'],
    use: [
      'Large rooms: air all round, 360°, without draughts',
      'Optional sensor panel: senses people and the floor temperature',
      'Optional auto grille lowers the filter: no ladder to clean it',
    ],
    limits: [
      'A large panel to work into the ceiling design',
      'Needs 13–15 inches above the ceiling',
      'A ceiling cut-out and access for service',
    ],
    specs: [
      ['Panel depth', 'Standard 50 mm, designer 97 mm, auto grille 105 mm'],
      ['Sound', '27–46 dB(A)'],
      ['Airflow', '10–35.5 m³/min'],
      ['Sensor', 'Senses people up to about 8.5 m across at a 2.7 m ceiling'],
    ],
  },
  {
    id: 'two-way-cassette', group: 'cassette', kind: 'cassette',
    name: '2-way cassette', series: 'FXCQ',
    sizes: [['FXCQ25BVM6', 9600], ['FXCQ32BVM6', 12300], ['FXCQ40BVM6', 15400], ['FXCQ50BVM6', 19100], ['FXCQ63BVM6', 24200], ['FXCQ80BVM6', 30700], ['FXCQ125BVM6', 47800]],
    unitMm: 305, plan: [15, 16],
    dims: ['305 × 775–1,445 × 620 mm', 'Panel 1,070–1,740 × 700 mm, 55 mm thick'],
    shows: 'A long, narrow panel',
    best: ['Corridors', 'Long rooms', 'Glass façades'],
    use: [
      'Long, narrow rooms: blows to both sides',
      'Along a window line or a glass façade',
      'Its drain pump lifts water 850 mm',
    ],
    limits: [
      'The deepest cassette: 305 mm unit',
      'A long panel, up to 1,740 mm, to line up with the ceiling',
    ],
    specs: [
      ['Sound', '29–46 dB(A)'],
      ['Airflow', 'Up to 32 m³/min'],
      ['Drain pump', 'Standard, lifts 850 mm'],
      ['Filter', 'Long-life: cleaned once a year'],
    ],
  },
  {
    id: 'ceiling-suspended', group: 'open', kind: 'susp',
    name: 'Ceiling suspended', series: 'FXHQ',
    sizes: [['FXHQ32MA', 12300], ['FXHQ63MA', 24200], ['FXHQ100MA', 38200], ['FXHQ125A', 48000], ['FXHQ140A', 52900]],
    unitMm: 195, unitText: '195–235 mm',
    dims: ['195 × 960–1,400 × 680 mm', '235 × 1,590 × 690 mm (largest)'],
    shows: 'A slim unit under the slab',
    best: ['Shops', 'Restaurants', 'High ceilings'],
    use: [
      'Exposed slabs with no false ceiling',
      'High ceilings, up to 4.3 m: it throws air far',
      'Quick retrofits in finished spaces',
    ],
    limits: ['The whole unit is on show', 'A wide unit: 960–1,590 mm'],
    specs: [
      ['Sound', '31–48 dB(A)'],
      ['Airflow', '10–36 m³/min'],
      ['Weight', '24–41 kg'],
    ],
  },
  {
    id: 'four-way-suspended', group: 'open', kind: 'susp4',
    name: '4-way ceiling suspended', series: 'FXUQ',
    sizes: [['FXUQ71AVEB', 27300], ['FXUQ100AVEB', 38200]],
    unitMm: 198,
    dims: ['198 × 950 × 950 mm'],
    shows: 'A 950 mm square unit with round corners',
    best: ['Cafés', 'Showrooms', 'Exposed ceilings'],
    use: [
      'The look of a cassette, with no false ceiling',
      'Air in 2, 3 or 4 directions, each flap set on its own',
      'Industrial and exposed ceilings',
    ],
    limits: ['Only two sizes', 'The unit is on show'],
    specs: [
      ['Sound', '36–47 dB(A)'],
      ['Drain pump', 'Standard, lifts 600 mm'],
    ],
  },
  {
    id: 'wall-mounted', group: 'open', kind: 'wall',
    name: 'Wall mounted', series: 'FXAQ',
    sizes: [['FXAQ20ARVE6', 7500], ['FXAQ25ARVE6', 9600], ['FXAQ32ARVE6', 12300], ['FXAQ40ARVE6', 15400], ['FXAQ50ARVE6', 19100], ['FXAQ63ARVE6', 24200], ['FXAQ71B', 30027], ['FXAQ80A', 34210], ['FXAQ90A', 37533]],
    unitMm: 298, unitText: '298–325 mm',
    dims: ['298 × 929 × 258 mm (0.6–2.0 TR)', '325 × 1,060–1,220 × 278 mm (2.5–3.1 TR)'],
    shows: 'The unit on the wall',
    best: ['Bedrooms', 'Small offices', 'Retrofits'],
    use: [
      'No false ceiling at all',
      'The simplest unit to fit and to service',
      'Retrofits in finished rooms',
    ],
    limits: [
      'On show on the wall',
      'The drain needs a route out of the room',
      'The larger sizes are louder, up to about 55 dB(A)',
    ],
    specs: [
      ['Sound', '31–47 dB(A) up to 2 TR; 44–55 dB(A) above'],
      ['Weight', '13–20 kg'],
      ['Panel', 'Flat: wipes clean in one pass'],
    ],
  },
  {
    id: 'floor-standing', group: 'floor', kind: 'floor',
    name: 'Floor standing', series: 'FXLQ',
    sizes: [['FXLQ32MAVE8', 12300], ['FXLQ50MAVE8', 19100], ['FXLQ63MAVE8', 24200]],
    unitMm: 600,
    dims: ['600 × 1,140 × 222 mm (1.0 TR)', '600 × 1,420 × 222 mm (1.6–2.0 TR)'],
    shows: 'A low unit against the wall',
    best: ['Under windows', 'Glass façades', 'Double-height rooms'],
    use: [
      'Under a window or along glass',
      'Rooms where neither the ceiling nor the wall can take a unit',
      'Winter heating: warm air from low down',
    ],
    limits: ['2 TR at most', 'Takes floor and wall space'],
    specs: [
      ['Sound', '32–40 dB(A)'],
      ['Airflow', '6–16 m³/min'],
      ['Weight', '30–36 kg'],
      ['Also', 'Can be hung on the wall, for easier cleaning'],
    ],
  },
  {
    id: 'concealed-floor', group: 'floor', kind: 'floorhidden',
    name: 'Concealed floor standing', series: 'FXNQ',
    sizes: [['FXNQ32MAVE8', 12300], ['FXNQ50MAVE8', 19100], ['FXNQ63MAVE8', 24200]],
    unitMm: 610, box: 'A box at least 220 mm (9 in) deep',
    dims: ['610 × 1,070 × 220 mm (1.0 TR)', '610 × 1,350 × 220 mm (1.6–2.0 TR)'],
    shows: 'Only the grilles in the boxing',
    best: ['Luxury living', 'Glass façades', 'Window seats'],
    use: [
      'Hidden in a window seat or a skirting box',
      'Glass façades with no wall or ceiling to use',
      'Warm air from low down in winter',
    ],
    limits: ['2 TR at most', 'Needs a box at least 220 mm deep, with grilles and an access panel'],
    specs: [
      ['Sound', '32–40 dB(A)'],
      ['Weight', '23–27 kg'],
      ['Pipes', 'Connect from below'],
    ],
  },
  {
    id: 'large-duct', group: 'big', kind: 'duct',
    name: 'Large duct', series: 'FXMQ-N',
    sizes: [['FXMQ170NVE6', 65800], ['FXMQ200NVE6', 76400], ['FXMQ250NVE6', 95500]],
    unitMm: 440, plan: [20, 21], ducts: true,
    dims: ['440 × 1,190–1,490 × 1,090 mm'],
    shows: 'Only the grilles',
    best: ['Halls', 'Banquets', 'Large shops'],
    use: ['One big space on one unit', 'Long duct runs with many grilles'],
    limits: [
      'A 440 mm unit: the deepest ceiling of all',
      'Heavy, 110–130 kg: hung from the slab on proper anchors',
      'Needs access for service',
    ],
    specs: [['Weight', '110–130 kg']],
  },
  {
    id: 'ahu-duct', group: 'big', kind: 'ahu',
    name: 'Floor standing duct (AHU type)', series: 'FXVQ',
    sizes: [['FXVQ125N', 47800], ['FXVQ200N', 76400], ['FXVQ250N', 95500], ['FXVQ400N', 154000], ['FXVQ500N', 191000]],
    room: 'A small AHU room, ducted to the space',
    dims: ['1,670 × 750–1,170 × 510 mm (4.0–8.0 TR)', '1,900 × 1,170–1,470 × 720 mm (12.8–15.9 TR)'],
    shows: 'Only the grilles',
    best: ['Auditoriums', 'Banquets', 'Factories'],
    use: [
      'Very large spaces from one unit, with ducts',
      'Runs on all fresh air, or fresh air mixed with return air',
    ],
    limits: ['Needs its own room and 3-phase power', '52–66 dB(A): needs acoustic treatment near quiet spaces'],
    specs: [
      ['Static pressure', '152–420 Pa'],
      ['Airflow', '43–172 m³/min'],
      ['Power', '3-phase, 380–415 V'],
      ['Drive', 'Belt drive, long-life filter'],
      ['Casing', 'Ivory white'],
    ],
  },
  {
    id: 'spot-cooler', group: 'big', kind: 'cube',
    name: 'Spot cooler (multi cube)', series: 'FXPQ',
    sizes: [['FXPQ25AVM', 9600]],
    unitMm: 455,
    dims: ['455 × 555 × 470 mm'],
    shows: 'A box on a stand, or hung',
    best: ['Workstations', 'Single spots'],
    use: ['Spot cooling where a room unit cannot go', 'Rests on a stand or hangs from the slab'],
    limits: ['One size only, 0.8 TR', '51 dB(A): louder than room units'],
    specs: [
      ['Sound', '51 dB(A)'],
      ['Airflow', '11–13.5 m³/min'],
      ['Weight', '30 kg'],
    ],
  },
];

// ─── helpers ───────────────────────────────────────────────────────────────
export const INCH_MM = 25.4;
export const tonsOf = (btu) => (Math.round(btu / 1200) / 10).toFixed(1);
const inchMm = (inch) => Math.round((inch * INCH_MM) / 5) * 5;

export function sizeRows(u) {
  return u.sizes.map(([model, btu]) => ({ model, btu, tr: tonsOf(btu), btuText: btu.toLocaleString('en-IN') }));
}

export function tonRange(u) {
  const t = u.sizes.map(([, btu]) => tonsOf(btu));
  return t.length === 1 ? `${t[0]} TR` : `${t[0]}–${t[t.length - 1]} TR`;
}

export const tonList = (u) => u.sizes.map(([, btu]) => tonsOf(btu)).join(', ');

// "8–9 in (205–230 mm)", or null when the unit needs no false ceiling.
export function planText(u) {
  if (!u.plan) return null;
  const [a, b] = u.plan;
  return `${a}–${b} in (${inchMm(a)}–${inchMm(b)} mm)`;
}
export const planShort = (u) => (u.plan ? `${u.plan[0]}–${u.plan[1]} in` : null);
export const planMm = (u) => (u.plan ? [inchMm(u.plan[0]), inchMm(u.plan[1])] : null);

// What the unit needs, in one line, for lists and tables.
export function spaceText(u) {
  if (u.plan) return `${planText(u)}${u.ducts ? ', plus ducts' : ''}`;
  if (u.box) return u.box;
  if (u.room) return u.room;
  return 'No false ceiling needed';
}
export function spacePill(u) {
  if (u.plan) return planShort(u);
  if (u.box) return '9 in box';
  if (u.room) return 'AHU room';
  return 'No ceiling';
}
export const unitHeightText = (u) => (u.unitText || (u.unitMm ? `${u.unitMm} mm` : '—'));

export const UNIT_RULE = 'Ceiling to plan for is Citi Cool site practice: Daikin’s ceiling figure (or the unit height plus 30 mm where Daikin gives none) plus 1 to 2 inches, so the unit can be serviced later. Add the depth of any ducts or beams crossing below.';
export const UNIT_SOURCE = 'Sizes, dimensions and specifications from Daikin’s VRV Alpha catalogue (the 4-way ceiling suspended unit’s cooling from Daikin’s data for the same models). Tons are Daikin’s rated cooling in Btu/h divided by 12,000. All 15 connect to Daikin VRV Alpha; VRV Home takes ducted and wall units.';

// Plain HTML for the pre-render: every unit in full, so Google reads it all.
export function indoorUnitsHtml(esc) {
  const li = (xs) => xs.map((x) => `<li>${esc(x)}</li>`).join('');
  return UNIT_GROUPS.map((g) => {
    const units = VRV_INDOOR_UNITS.filter((u) => u.group === g.id);
    return `<h3>${esc(g.title)}</h3><p>${esc(g.sub)}.</p>${units.map((u) => `
      <h4>${esc(u.name)} (Daikin ${esc(u.series)}): ${esc(tonRange(u))}</h4>
      <ul>
        <li>Ceiling to plan for: ${esc(spaceText(u))}</li>
        <li>Unit height: ${esc(unitHeightText(u))}${u.unitNote ? ` (${esc(u.unitNote)})` : ''}</li>
        <li>Dimensions (H × W × D): ${esc(u.dims.join('; '))}</li>
        <li>Sizes: ${esc(tonList(u))} TR</li>
        <li>What shows in the room: ${esc(u.shows)}</li>
        <li>Best for: ${esc(u.best.join(', '))}</li>
      </ul>
      <p>Use it when:</p><ul>${li(u.use)}</ul>
      <p>Limitations:</p><ul>${li(u.limits)}</ul>
      <table><thead><tr><th>Tons</th><th>Btu/h</th><th>Model</th></tr></thead><tbody>${sizeRows(u).map((s) => `<tr><td>${s.tr} TR</td><td>${s.btuText}</td><td>${esc(s.model)}</td></tr>`).join('')}</tbody></table>
      ${u.specs.length ? `<ul>${u.specs.map(([k, v]) => `<li>${esc(k)}: ${esc(v)}</li>`).join('')}</ul>` : ''}`).join('')}`;
  }).join('') + `<p><small>${esc(UNIT_RULE)} ${esc(UNIT_SOURCE)}</small></p>`;
}

// A compact table of all 15, for the VRV page's crawler copy.
export function indoorUnitsTableHtml(esc) {
  return `<table><thead><tr><th>Indoor unit</th><th>Daikin</th><th>Tons</th><th>Ceiling to plan for</th><th>Use it for</th><th>Main limit</th></tr></thead><tbody>${VRV_INDOOR_UNITS.map((u) => `<tr><td>${esc(u.name)}</td><td>${esc(u.series)}</td><td>${esc(tonRange(u))}</td><td>${esc(spaceText(u))}</td><td>${esc(u.best.join(', '))}</td><td>${esc(u.limits[0])}</td></tr>`).join('')}</tbody></table>`;
}

// ─── The look: for customers on the VRV page ────────────────────────────────
// What each unit looks like in the room, its colours and its finish.
// Customers care about the look more than the ceiling it takes, so the VRV
// page leads with this; ceiling depths stay with the architects.
//
// Every line is sourced: src 'daikin' = Daikin India VRV Alpha catalogue
// (words in quotes are Daikin's own; checked 11 Oct 2026), src 'citicool' =
// Citi Cool site practice or what is on show at our Experience Center.
// Swatch colours are only indicative of Daikin's named colours.
//
// room: which drawing components/RoomView.jsx draws. img: the unit's own
// image and photo: Daikin's photo of it in a room, both cut from the same
// catalogue (public/media/vrv-indoor/<name>.webp). A colour can carry its own
// img and photo (the 1-way cassette's three panels, the round flow's two).

export const LOOKS = [
  { id: 'hidden', title: 'Hidden', sub: 'Only the grilles show', room: 'duct' },
  { id: 'panel', title: 'A panel in the ceiling', sub: 'Flat panels in white, silver or black', room: 'square' },
  { id: 'onshow', title: 'No false ceiling', sub: 'On the wall, or under the slab', room: 'wall' },
  { id: 'floor', title: 'Low on the floor', sub: 'Under a window, or in boxing', room: 'floor' },
  { id: 'big', title: 'Big spaces', sub: 'Halls, banquets and single spots', room: 'hall' },
];

const GRILLE_COLOURS = [
  { name: 'White', sw: '#ffffff', edge: '#cbd5e1', edgeW: 1, grille: '#ffffff', slot: '#64748b' },
  { name: 'Black', sw: '#1f2937', edge: '#1f2937', edgeW: 1, grille: '#1f2937', slot: '#9ca3af' },
];
const GRILLE_NOTE = 'Your choice. Colours on screen are indicative.';
const FRESH_WHITE = { name: 'Fresh White', sw: '#f4f3ee', edge: '#cbd5e1', edgeW: 1, panel: '#f4f3ee', grille: '#e7e5df', slot: '#57534e' };
const D = 'daikin';
const C = 'citicool';

export const UNIT_LOOKS = {
  'slim-duct': {
    look: 'hidden', room: 'duct', img: 'fxdq', photo: { src: 'room-fxdq', cap: 'A hotel room: only the grilles show' }, tag: 'Hidden', falseCeiling: 'Needed',
    see: 'Only the grilles. The unit sits above the false ceiling, out of sight.',
    quote: 'Suitable for installation in limited spaces like drop-ceilings in hotels.',
    finish: [
      ['Daikin lists the grilles as “to be obtained locally”, so you choose their style and colour.', D],
      ['Linear slots along a wall, or square and round diffusers, in white or black: all on show at our Experience Center.', C],
      ['One of the quietest units: 29–36 dB(A).', D],
    ],
    colourLabel: 'Grille colour', colours: GRILLE_COLOURS, colourNote: GRILLE_NOTE,
  },
  'mid-duct': {
    look: 'hidden', room: 'duct', img: 'fxmq', photo: { src: 'room-fxmq', cap: 'Round diffusers in an office ceiling' }, tag: 'Hidden', falseCeiling: 'Needed',
    see: 'Only the grilles. A short duct takes the air to two or three grilles.',
    finish: [
      ['Grilles in the style and colour you choose: linear slots, square or round diffusers.', C],
      ['Linear slot diffusers can run along a window or a wall.', C],
    ],
    clean: [['The drain pan can be detached for easy cleaning.', D]],
    colourLabel: 'Grille colour', colours: GRILLE_COLOURS, colourNote: GRILLE_NOTE,
  },
  'high-duct': {
    look: 'hidden', room: 'duct', img: 'fxmq', photo: { src: 'room-fxmq', cap: 'Round diffusers in an office ceiling' }, tag: 'Hidden', falseCeiling: 'Needed',
    see: 'Only the grilles. One unit feeds many grilles through ducts.',
    finish: [
      ['The unit can sit over a passage or a toilet, away from the room.', C],
      ['Grilles in the style and colour you choose.', C],
    ],
    clean: [['The drain pan can be detached for easy cleaning.', D]],
    colourLabel: 'Grille colour', colours: GRILLE_COLOURS, colourNote: GRILLE_NOTE,
  },
  'one-way-cassette': {
    look: 'panel', room: 'corner', img: 'fxkq-white', tag: 'Slim panel', falseCeiling: 'Needed',
    see: 'A slim panel along the wall or in a corner: 1,390 × 595 mm, 41 mm thick.',
    quote: 'Sleek panel with dual tone styling.',
    finish: [
      ['Three panels: all white, white on a black base, or silver on a black base.', D],
      ['A seamless fit along a wall or in a corner, so the rest of the ceiling stays clear.', C],
    ],
    colourLabel: 'Panel', colourNote: 'Colours on screen are indicative.',
    colours: [
      { name: 'White', sw: '#f8fafc', edge: '#cbd5e1', edgeW: 1, panel: '#f8fafc', base: '#f1f5f9', perf: '#cbd5e1', slot: '#64748b', img: 'fxkq-white' },
      { name: 'White on black', sw: 'linear-gradient(135deg, #f8fafc 50%, #1f2937 50%)', edge: '#94a3b8', edgeW: 1, panel: '#f8fafc', base: '#1f2937', perf: '#4b5563', slot: '#64748b', img: 'fxkq-white-black' },
      { name: 'Silver on black', sw: 'linear-gradient(135deg, #cbd5e1 50%, #1f2937 50%)', edge: '#94a3b8', edgeW: 1, panel: '#cbd5e1', base: '#1f2937', perf: '#4b5563', slot: '#475569', img: 'fxkq-silver-black' },
    ],
    photo: { src: 'room-fxkq', cap: 'Along the wall of a living room' },
  },
  'compact-cassette': {
    look: 'panel', room: 'tile', img: 'fxzq', photo: { src: 'room-fxzq', cap: 'Flush in a 600 × 600 grid ceiling' }, tag: 'Flush tile', falseCeiling: 'Needed',
    see: 'One 600 × 600 ceiling tile, only 8 mm below the ceiling.',
    quote: 'Fully-flat integration in standard architectural ceiling tiles, leaving only 8mm.',
    finish: [
      ['“An elegant finish in white.”', D],
      ['Lights, speakers and sprinklers can sit in the next tile.', D],
    ],
    clean: [['It keeps air off the ceiling “to prevent ceiling stains”.', D]],
    colourLabel: 'Panel', colourNote: 'Colours on screen are indicative.',
    colours: [{ name: 'White', sw: '#f8fafc', edge: '#cbd5e1', edgeW: 1, panel: '#f8fafc', grille: '#eef2f7', slot: '#475569' }],
  },
  'round-flow-cassette': {
    look: 'panel', room: 'square', img: 'fxfsq-white', tag: 'Square panel', falseCeiling: 'Needed',
    see: 'A 950 × 950 mm square panel in the ceiling, 50 mm deep.',
    quote: 'Designer choice has been given a boost with the increase in number of new types of decoration panels.',
    finish: [
      ['Standard and sensing panels in Fresh White or Black.', D],
      ['Designer and auto grille panels in Fresh White.', D],
      ['Air all round, 360°.', D],
      ['Sensing panel: senses people and the floor temperature.', D],
    ],
    clean: [['Auto grille panel: the filter comes down for cleaning, “without the need for a step ladder”.', D]],
    colourLabel: 'Panel', colourNote: 'Colours on screen are indicative. See both at our Experience Center.',
    colours: [
      { ...FRESH_WHITE, img: 'fxfsq-white', photo: { src: 'room-fxfsq-white', cap: 'Fresh White panel in a meeting room' } },
      { name: 'Black', sw: '#1f2937', edge: '#1f2937', edgeW: 1, panel: '#1f2937', grille: '#374151', slot: '#9ca3af', img: 'fxfsq-black', photo: { src: 'room-fxfsq-black', cap: 'Black panel in a dark ceiling' } },
    ],
  },
  'two-way-cassette': {
    look: 'panel', room: 'long', img: 'fxcq', photo: { src: 'room-fxcq', cap: 'Long panels in an open office' }, tag: 'Long panel', falseCeiling: 'Needed',
    see: 'A long, narrow panel: 1,070–1,740 × 700 mm, 55 mm thick.',
    quote: 'Stylish unit blends easily with any interior.',
    finish: [
      ['Fresh White panel.', D],
      ['A flat flap gives an “integrated ceiling surface”.', D],
    ],
    off: [['“The flat flaps close entirely when the unit is not operating and there are no air intake grilles visible.”', D]],
    clean: [['Long-life filter: “only 1-year maintenance interval”.', D]],
    colourLabel: 'Panel', colours: [FRESH_WHITE], colourNote: 'Colours on screen are indicative.',
  },
  'ceiling-suspended': {
    look: 'onshow', room: 'susp', img: 'fxhq', photo: { src: 'room-fxhq', cap: 'Under the ceiling of a shop' }, tag: 'On show', falseCeiling: 'Not needed',
    see: 'A slim unit hung under the slab: 195–235 mm high, 960–1,590 mm wide.',
    quote: 'Slim body with quiet and wide airflow.',
    finish: [
      ['No false ceiling: for exposed slabs and high ceilings, up to 4.3 m.', D],
      ['Air swings up and down; the louvres set left and right by hand.', D],
    ],
    off: [['“Flap neatly closes when not in use.”', D]],
  },
  'four-way-suspended': {
    look: 'onshow', room: 'susp4', img: 'fxuq', photo: { src: 'room-fxuq', cap: 'Under the slab of a clothes shop' }, tag: 'On show', falseCeiling: 'Not needed',
    see: 'A 950 × 950 mm square unit with round corners, 198 mm high, under the slab.',
    quote: 'Unit body and suction panel adopted round shapes and realized a slim appearance design.',
    finish: [
      ['Fresh White.', D],
      ['The look of a cassette, with no false ceiling.', C],
      ['Air in 2, 3 or 4 directions, each flap set on its own.', D],
    ],
    off: [['“Flaps close automatically when the unit stops, which gives a simple appearance.”', D]],
    colourLabel: 'Colour', colours: [FRESH_WHITE], colourNote: 'Colours on screen are indicative.',
  },
  'wall-mounted': {
    look: 'onshow', room: 'wall', img: 'fxaq', photo: { src: 'room-fxaq', cap: 'On a living room wall' }, tag: 'On the wall', falseCeiling: 'Not needed',
    see: 'The unit on the wall: 298 × 929 mm up to 2 TR, 325 × 1,060–1,220 mm above that.',
    quote: 'Stylish flat panel design harmonised with your interior decor.',
    finish: [
      ['White (N9.5).', D],
      ['Nothing in the ceiling: the simplest unit to fit in a finished room.', C],
    ],
    off: [['“The louvre closes automatically when the unit stops.”', D]],
    clean: [['“Flat panel can be cleaned with only the single pass of a cloth.”', D]],
    colourLabel: 'Colour', colourNote: 'Colours on screen are indicative.',
    colours: [{ name: 'White', sw: '#f2f2f2', edge: '#cbd5e1', edgeW: 1, panel: '#f2f2f2', slot: '#64748b' }],
  },
  'floor-standing': {
    look: 'floor', room: 'floor', img: 'fxlq', tag: 'On the floor', falseCeiling: 'Not needed',
    see: 'A low unit under a window: 600 mm high, 1,140–1,420 mm wide, 222 mm deep.',
    finish: [
      ['Ivory white casing.', D],
      ['It can also be hung on the wall, which makes cleaning under it easier.', D],
    ],
    clean: [['Long-life filter as standard.', D]],
    colourLabel: 'Colour', colourNote: 'Colours on screen are indicative.',
    colours: [{ name: 'Ivory white', sw: '#d9d5c7', edge: '#a8a29e', edgeW: 1, panel: '#e7e3d6', grille: '#d6d2c4', slot: '#78716c' }],
  },
  'concealed-floor': {
    look: 'floor', room: 'floorhidden', img: 'fxnq', photo: { src: 'room-fxnq', cap: 'In wooden boxing under a window, with a louvred grille' }, tag: 'Hidden in boxing', falseCeiling: 'Not needed',
    see: 'Only the grilles in your boxing. The unit hides in a window seat or a skirting box, at least 220 mm (9 in) deep.',
    quote: 'Designed to be concealed in the perimeter skirting-wall … that creates a classy interior design.',
    finish: [
      ['The box and its grilles are finished to match your interiors.', C],
      ['An access panel in the box, for service.', C],
    ],
    clean: [['Long-life filter as standard.', D]],
    finishNote: 'Your joinery and your grilles. The unit stays out of sight.',
  },
  'large-duct': {
    look: 'big', room: 'hall', img: 'fxmq-n', tag: 'Hidden', falseCeiling: 'Needed',
    see: 'Only the grilles. One unit feeds a whole hall through ducts.',
    finish: [['Grilles in the style and colour you choose.', C]],
    colourLabel: 'Grille colour', colours: GRILLE_COLOURS, colourNote: GRILLE_NOTE,
  },
  'ahu-duct': {
    look: 'big', room: 'hall', img: 'fxvq', tag: 'Hidden', falseCeiling: 'For the ducts',
    see: 'Only the grilles. The unit stands in its own small room, ducted to the space.',
    quote: 'Flexible interior design for each customer.',
    finish: [
      ['Grilles in the style and colour you choose.', C],
      ['Runs on all fresh air, or fresh air mixed with return air.', D],
    ],
    colourLabel: 'Grille colour', colours: GRILLE_COLOURS, colourNote: GRILLE_NOTE,
  },
  'spot-cooler': {
    look: 'big', room: 'cube', img: 'fxpq', photo: { src: 'room-fxpq', cap: 'Spot cooling in a factory, at Daikin’s own plant' }, tag: 'On show', falseCeiling: 'Not needed',
    see: 'A box, 455 × 555 × 470 mm, on a stand or hung from the slab.',
    finish: [['Spot cooling where a room unit cannot go.', C]],
  },
};

export const LOOK_IMG = (name) => `/media/vrv-indoor/${name}.webp`;

export const LOOK_SOURCE = 'Colours, panel sizes and the words in quotes are from Daikin’s VRV Alpha catalogue, and so are the unit images and room photos. Colours on screen are indicative: see the real finish at our Experience Center.';

// Each unit with its look, in the order of LOOKS.
export function unitsByLook() {
  return LOOKS.map((l) => ({
    ...l,
    units: VRV_INDOOR_UNITS.filter((u) => UNIT_LOOKS[u.id] && UNIT_LOOKS[u.id].look === l.id)
      .map((u) => ({ ...u, ...UNIT_LOOKS[u.id] })),
  }));
}

// Plain HTML for the pre-render of the VRV page: every unit's look in full.
export function unitLooksHtml(esc) {
  const srcText = (s) => (s === D ? 'Daikin' : 'Citi Cool');
  const li = (xs) => (xs || []).map(([t, s]) => `<li>${esc(t)} <small>(${srcText(s)})</small></li>`).join('');
  return unitsByLook().map((l) => `<h3>${esc(l.title)}: ${esc(l.sub.toLowerCase())}</h3>${l.units.map((u) => `
      <h4>${esc(u.name)} (Daikin ${esc(u.series)}): ${esc(tonRange(u))}</h4>
      <img src="${LOOK_IMG(u.img)}" alt="Daikin ${esc(u.series)} ${esc(u.name)}" loading="lazy" width="320">
      <p>What you see in the room: ${esc(u.see)}</p>
      ${u.quote ? `<blockquote>“${esc(u.quote)}” (Daikin, VRV Alpha catalogue)</blockquote>` : ''}
      ${u.colours ? `<p>${esc(u.colourLabel)}: ${esc(u.colours.map((c) => c.name).join(', '))}</p>` : ''}
      ${u.finishNote ? `<p>Finish: ${esc(u.finishNote)}</p>` : ''}
      <p>Finish and look:</p><ul>${li(u.finish)}</ul>
      ${u.off ? `<p>When it is switched off:</p><ul>${li(u.off)}</ul>` : ''}
      ${u.clean ? `<p>Keeping it clean:</p><ul>${li(u.clean)}</ul>` : ''}
      <p>False ceiling: ${esc(u.falseCeiling)}. Best for: ${esc(u.best.join(', '))}.</p>`).join('')}`).join('') + `<p><small>${esc(LOOK_SOURCE)}</small></p>`;
}
